import * as React from 'react';
import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import Loader from 'shared/components/loader';
import PublicRoute from 'routes/public-routes';
import ProtectedRoute from 'routes/protected-routes';
import RootLayout from 'shared/components/root-layout';
import { ROUTES } from 'routes/constants';
import { PermissionsAdminEnum } from 'features/auth/enums/permissions-admin.enum';
import ErrorBoundary from 'shared/components/error-boundary';

import CreateAdminRolePage from 'pages/create-admin-role-page';
import UpdateAdminRolePage from 'pages/update-admin-role-page';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TestErrorPage from 'pages/test-error-page';
import PrefixRedirect from 'routes/prefix-redirect';

const AuthPage = lazy(() => import('pages/auth'));
const NotFoundPage = lazy(() => import('pages/not-found'));
const HomePage = lazy(() => import('pages/home'));

const AdminUsersPage = lazy(() => import('pages/admin-users-page'));
const AdminRolesPage = lazy(() => import('pages/admin-roles-page'));
const CreateAdminUserPage = lazy(() => import('pages/create-admin-user-page'));
const UpdateAdminUserPage = lazy(() => import('pages/update-admin-user-page'));

const VerifyEmailPage = lazy(() => import('pages/verify-email.page'));
const EmailVerificationRoute = lazy(
  () => import('routes/email-verification-route')
);
const GcashHomePage = lazy(() => import('pages/gcash-home-page'));
const GcashApplicationsPage = lazy(
  () => import('pages/gcash-applications-page')
);
const GcashApplicationPage = lazy(() => import('pages/gcash-application-page'));
const GcashDocumentPage = lazy(() => import('pages/gcash-document-page'));
const GcashActivitiesPage = lazy(() => import('pages/gcash-activities-page'));
const GcashReportsPage = lazy(() => import('pages/gcash-reports-page'));
const AdminLogsPage = lazy(() => import('pages/admin-logs-page'));

const AppRoutes = () => {
  return (
    <Suspense fallback={<Loader />}>
      <ToastContainer limit={3} position={'bottom-right'} />
      <PrefixRedirect />
      <ErrorBoundary>
        <RootLayout>
          <Routes>
            <Route
              path={ROUTES.home}
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.testErrorPage}
              element={
                <ProtectedRoute>
                  <TestErrorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.dynamic.signIn()}
              element={
                <PublicRoute>
                  <AuthPage />
                </PublicRoute>
              }
            />
            <Route
              path={ROUTES.adminUsers}
              element={
                <ProtectedRoute
                  allowedPermissions={[
                    PermissionsAdminEnum.SUPER_ADMIN,
                    PermissionsAdminEnum.ADMIN_MAIN,
                  ]}
                >
                  <AdminUsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.adminRoles}
              element={
                <ProtectedRoute
                  allowedPermissions={[
                    PermissionsAdminEnum.SUPER_ADMIN,
                    PermissionsAdminEnum.ADMIN_MAIN,
                  ]}
                >
                  <AdminRolesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.adminLogs}
              element={
                <ProtectedRoute
                  allowedPermissions={[
                    PermissionsAdminEnum.SUPER_ADMIN,
                    PermissionsAdminEnum.ADMIN_MAIN,
                  ]}
                >
                  <AdminLogsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.createAdminUser}
              element={
                <ProtectedRoute
                  allowedPermissions={[
                    PermissionsAdminEnum.SUPER_ADMIN,
                    PermissionsAdminEnum.ADMIN_MAIN,
                  ]}
                >
                  <CreateAdminUserPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.dynamic.updateAdminUser()}
              element={
                <ProtectedRoute
                  allowedPermissions={[
                    PermissionsAdminEnum.SUPER_ADMIN,
                    PermissionsAdminEnum.ADMIN_MAIN,
                  ]}
                >
                  <UpdateAdminUserPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.createAdminRole}
              element={
                <ProtectedRoute
                  allowedPermissions={[
                    PermissionsAdminEnum.SUPER_ADMIN,
                    PermissionsAdminEnum.ADMIN_MAIN,
                  ]}
                >
                  <CreateAdminRolePage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.dynamic.updateAdminRole()}
              element={
                <ProtectedRoute
                  allowedPermissions={[
                    PermissionsAdminEnum.SUPER_ADMIN,
                    PermissionsAdminEnum.ADMIN_MAIN,
                  ]}
                >
                  <UpdateAdminRolePage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.gcashHomePage}
              element={
                <ProtectedRoute
                  allowedPermissions={[PermissionsAdminEnum.VIEW_HOME_PAGE]}
                >
                  <GcashHomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.gcashApplications}
              element={
                <ProtectedRoute
                  allowedPermissions={[
                    PermissionsAdminEnum.VIEW_APPLICATIONS_LIST,
                  ]}
                >
                  <GcashApplicationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.dynamic.gcashApplication()}
              element={
                <ProtectedRoute
                  allowedPermissions={[
                    PermissionsAdminEnum.VIEW_SINGLE_APPLICATION,
                  ]}
                >
                  <GcashApplicationPage />
                </ProtectedRoute>
              }
            />{' '}
            <Route
              path={ROUTES.dynamic.gcashDocument()}
              element={
                <ProtectedRoute
                  allowedPermissions={[
                    PermissionsAdminEnum.VIEW_SINGLE_DOCUMENT,
                  ]}
                >
                  <GcashDocumentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.gcashReports}
              element={
                <ProtectedRoute
                  allowedPermissions={[PermissionsAdminEnum.VIEW_REPORTS]}
                >
                  <GcashReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.gcashActivities}
              element={
                <ProtectedRoute
                  allowedPermissions={[PermissionsAdminEnum.VIEW_ACTIVITIES]}
                >
                  <GcashActivitiesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.verifyEmail}
              element={
                <EmailVerificationRoute>
                  <VerifyEmailPage />
                </EmailVerificationRoute>
              }
            />
            <Route path={ROUTES.another} element={<NotFoundPage />} />
          </Routes>
        </RootLayout>
      </ErrorBoundary>
    </Suspense>
  );
};

export default AppRoutes;
